// Regular Expressions course — Part 3: Advanced Patterns + a Regex Golf challenge.
// Verified against the V8/JS regex engine used by the Scittle runtime.

export const regexPart3 = [
  {
    id: "flags",
    title: "Flags: Changing the Rules",
    group: "Advanced Patterns",
    summary: "Case-insensitive, multiline and dot-all matching.",
    content: [
      { t: "p", text: "Flags are switches that change how a whole pattern behaves. The most common is 'ignore case', so CAT, cat and Cat all match." },
      { t: "p", text: "In real ClojureScript, the #\"...\" literal doesn't carry flags. To add them, build the pattern with js/RegExp, passing the flag letters as a second string. The i flag means 'ignore case'." },
      { t: "note", text: "Worth knowing: this browser sandbox is lenient and also accepts JVM-style inline flags right inside the literal — #\"(?i)cat\" matches \"CAT\" here, and (?m) and (?s) work too. But that's a Java-ism: real ClojureScript compiles to JavaScript, and JavaScript patterns can't carry inline flags, so they'd fail there. Build with js/RegExp as shown — it's the form that works everywhere." },
      { t: "code", code: "(re-find (js/RegExp. \"cat\" \"i\") \"The CAT sat\")" },
      { t: "p", text: "It works with re-seq too — here we grab every vowel regardless of case:" },
      { t: "code", code: "(re-seq (js/RegExp. \"[aeiou]\" \"i\") \"AEIou\")" },
      { t: "h", text: "The common flags" },
      { t: "list", items: [
        "i — ignore case (CAT = cat).",
        "m — multiline: ^ and $ match the start/end of each LINE, not just the whole string.",
        "s — dot-all: the dot . also matches newline characters.",
        "g — global: find every match (many tools set this automatically).",
      ] },
      { t: "h", text: "See the m flag in action" },
      { t: "p", text: "By default, ^ anchors to the very start of the whole string. So a pattern looking for a b at the start of the SECOND line finds nothing:" },
      { t: "code", code: "(re-find #\"^b\" \"alpha\\nbeta\")" },
      { t: "p", text: "Add the m flag, and ^ now matches the start of every line:" },
      { t: "code", code: "(re-find (js/RegExp. \"^b\" \"m\") \"alpha\\nbeta\")" },
      { t: "p", text: "The same goes for $, which normally anchors to the end of the whole string:" },
      { t: "code", code: "(re-find #\"one$\" \"line one\\nline two\")" },
      { t: "p", text: "…but with m, $ matches the end of every line:" },
      { t: "code", code: "(re-find (js/RegExp. \"one$\" \"m\") \"line one\\nline two\")" },
      { t: "note", text: "Writing a pattern as a plain string means backslashes must be doubled: use \"\\\\d\" to mean \\d, or just use a class like [0-9]. That's why the #\"...\" literal (no doubling needed) is nicer when you don't need flags." },
      { t: "code", code: "(re-seq (js/RegExp. \"[0-9]+\" \"\") \"a12 b34 c5\")" },
    ],
    exercises: [
      {
        prompt: "Use re-seq with a case-insensitive pattern to find every \"the\" in \"THE the ThE\". Expected: (\"THE\" \"the\" \"ThE\").",
        starter: "(re-seq (js/RegExp. \"the\" \"\") \"THE the ThE\")",
        tests: ["(= answer '(\"THE\" \"the\" \"ThE\"))"],
        solution: "(re-seq (js/RegExp. \"the\" \"i\") \"THE the ThE\")",
        hint: "Add the i flag as the second argument: (js/RegExp. \"the\" \"i\").",
      },
      {
        prompt: "Now the m flag. Write the expression that builds a pattern matching a word at the END of a line, using js/RegExp with the m flag. It should match \"one\" in \"line one\\nline two\" but find nothing in the one-line \"line one two\".",
        starter: "(js/RegExp. \"\" \"\")",
        tests: ["(= (re-find answer \"line one\\nline two\") \"one\")", "(nil? (re-find answer \"line one two\"))"],
        solution: "(js/RegExp. \"one$\" \"m\")",
        hint: "The m flag makes $ match the end of each line: (js/RegExp. \"one$\" \"m\").",
      },
    ],
  },

  {
    id: "global-flag",
    title: "Deep-Dive: The Global Flag",
    group: "Advanced Patterns",
    summary: "How 'match all' works in replace, re-seq, and raw JS.",
    content: [
      { t: "p", text: "The g (global) flag means 'find every match, not just the first'. In ClojureScript you rarely write it yourself, because the tools are built to do the right thing — but understanding it prevents subtle bugs." },
      { t: "h", text: "str/replace already replaces ALL" },
      { t: "p", text: "clojure.string/replace replaces every match for you (it applies the global flag internally). So this changes every 'a':" },
      { t: "code", code: "(str/replace \"Banana\" #\"a\" \"o\")" },
      { t: "p", text: "Want only the FIRST match changed? Use str/replace-first." },
      { t: "code", code: "(str/replace-first \"Banana\" #\"a\" \"o\")" },
      { t: "h", text: "re-seq returns every match too" },
      { t: "p", text: "re-seq gives you all non-overlapping matches without you thinking about the g flag at all." },
      { t: "code", code: "(re-seq #\"\\d+\" \"1 22 333\")" },
      { t: "h", text: "Controlling flags yourself with interop" },
      { t: "p", text: "When you drop to raw JavaScript (via interop), YOU choose the flags. Here we call JS's own .replace with an explicit \"gi\" — global + case-insensitive — to replace every 'cat' regardless of case:" },
      { t: "code", code: "(.replace \"Cat cat CAT\" (js/RegExp. \"cat\" \"gi\") \"dog\")" },
      { t: "h", text: "The classic gotcha: a stateful global regex" },
      { t: "p", text: "A global js/RegExp remembers where it left off (its lastIndex) between calls to .test / .exec. Reusing one can give surprising results — watch the SAME test run twice return different answers:" },
      { t: "code", code: "(let [re (js/RegExp. \"[0-9]\" \"g\")]\n  [(.test re \"a1\") (.test re \"a1\")])" },
      { t: "note", text: "Takeaways: str/replace = all, str/replace-first = first, re-seq = all. Only reach for the g flag manually when using raw JS interop — and never reuse a global regex object across separate .test/.exec calls unless you mean to." },
    ],
    exercises: [
      {
        prompt: "Use str/replace to turn EVERY 'o' in \"foo boo zoo\" into '0'. Expected: \"f00 b00 z00\".",
        starter: "(str/replace \"foo boo zoo\" #\"\" \"0\")",
        tests: ["(= answer \"f00 b00 z00\")"],
        solution: "(str/replace \"foo boo zoo\" #\"o\" \"0\")",
        hint: "str/replace already replaces all matches: pattern #\"o\".",
      },
      {
        prompt: "Now change only the FIRST 'o' in \"foo boo zoo\". Expected: \"f0o boo zoo\".",
        starter: "(str/replace-first \"foo boo zoo\" #\"o\" \"\")",
        tests: ["(= answer \"f0o boo zoo\")"],
        solution: "(str/replace-first \"foo boo zoo\" #\"o\" \"0\")",
        hint: "Use str/replace-first instead of str/replace.",
      },
    ],
  },

  {
    id: "lookarounds",
    title: "Lookahead & Lookbehind",
    group: "Advanced Patterns",
    summary: "Match based on what's around — without consuming it.",
    content: [
      { t: "p", text: "Sometimes you want to match something only because of what comes before or after it — but you don't want that context included in the result. Lookarounds do exactly this. Like anchors, they match a POSITION, not characters." },
      { t: "h", text: "Positive lookahead (?=…)" },
      { t: "p", text: "\\d+(?=kg) matches a number only if it is followed by 'kg' — and 'kg' is NOT part of the match." },
      { t: "code", code: "(re-find #\"\\d+(?=kg)\" \"lift 100kg then 50lb\")" },
      { t: "code", code: "(re-seq #\"\\d+(?=kg)\" \"3kg 4lb 5kg\")" },
      { t: "h", text: "Negative lookahead (?!…)" },
      { t: "p", text: "cat(?!\\d) matches 'cat' only when it is NOT followed by a digit. Here the first 'cat' is followed by 1, so it's skipped; the 'cat' in 'caterpillar' matches." },
      { t: "code", code: "(re-find #\"cat(?!\\d)\" \"cat123 caterpillar\")" },
      { t: "h", text: "Lookbehind (?<=…) and (?<!…)" },
      { t: "p", text: "Lookbehind checks what comes BEFORE. (?<=\\$)\\d+ matches digits that follow a dollar sign, without including the sign." },
      { t: "code", code: "(re-find #\"(?<=\\$)\\d+\" \"price $42 and 7\")" },
      { t: "p", text: "The negative form (?<!…) matches when the preceding text is absent. This pulls numbers that are NOT preceded by a $ sign:" },
      { t: "code", code: "(re-seq #\"(?<!\\$)\\b\\d+\" \"$5 and 7 and $9 and 12\")" },
      { t: "note", text: "Lookbehind is a relatively recent regex feature. It works in modern browsers and JavaScript engines (which is what runs here), but very old environments may not support it." },
    ],
    exercises: [
      {
        prompt: "Using a positive lookahead, extract just the number that is followed by '%' from \"tax 20% and code 30\". Expected: \"20\".",
        starter: "(re-find #\"\" \"tax 20% and code 30\")",
        tests: ["(= answer \"20\")"],
        solution: "(re-find #\"\\d+(?=%)\" \"tax 20% and code 30\")",
        hint: "Match digits followed by a percent sign: #\"\\d+(?=%)\".",
      },
      {
        prompt: "Use a NEGATIVE lookahead: define a pattern (answer) that matches \"cat\" only when it is NOT followed by a digit. It should match in \"caterpillar\" but not in \"cat5\".",
        starter: "#\"\"",
        tests: ["(= (re-find answer \"caterpillar\") \"cat\")", "(nil? (re-find answer \"cat5\"))"],
        solution: "#\"cat(?!\\d)\"",
        hint: "Negative lookahead (?!…): cat(?!\\d) — cat not followed by a digit.",
      },
    ],
  },

  {
    id: "named-groups",
    title: "Named Groups",
    group: "Advanced Patterns",
    summary: "Give your capture groups readable names.",
    content: [
      { t: "p", text: "As patterns grow, remembering that 'group 2 is the month' gets error-prone. Named groups let you label a group with (?<name>…) so the pattern documents itself." },
      { t: "code", code: "(re-find #\"(?<year>\\d{4})-(?<month>\\d{2})-(?<day>\\d{2})\" \"2026-06-15\")" },
      { t: "p", text: "In ClojureScript, re-find still returns the groups POSITIONALLY (whole match, then each group), so you usually read them by destructuring — the names are for readability in the pattern." },
      { t: "code", code: "(let [[_ y m d] (re-find #\"(?<year>\\d{4})-(?<month>\\d{2})-(?<day>\\d{2})\" \"2026-06-15\")]\n  {:year y :month m :day d})" },
      { t: "h", text: "Names shine in replacements" },
      { t: "p", text: "Where named groups really pay off is str/replace: refer to a captured group by name with $<name> in the replacement string. Reformatting becomes self-explanatory." },
      { t: "code", code: "(str/replace \"2026-06-15\"\n  #\"(?<y>\\d{4})-(?<m>\\d{2})-(?<d>\\d{2})\"\n  \"$<d>/$<m>/$<y>\")" },
      { t: "h", text: "Bonus: reading a group by name" },
      { t: "p", text: "If you really want name-based access to a match, drop into interop: a pattern's .exec returns a match whose .groups object holds the named captures." },
      { t: "code", code: "(.. (.exec #\"(?<year>\\d{4})\" \"2026\") -groups -year)" },
      { t: "note", text: "Rule of thumb: use named groups for clarity in the pattern and in $<name> replacements; read matches positionally with destructuring in ClojureScript." },
    ],
    exercises: [
      {
        prompt: "Use str/replace with named groups to turn \"2026-06-15\" into \"15-06-2026\". Expected: \"15-06-2026\".",
        starter: "(str/replace \"2026-06-15\"\n  #\"(?<y>\\d{4})-(?<m>\\d{2})-(?<d>\\d{2})\"\n  \"\")",
        tests: ["(= answer \"15-06-2026\")"],
        solution: "(str/replace \"2026-06-15\"\n  #\"(?<y>\\d{4})-(?<m>\\d{2})-(?<d>\\d{2})\"\n  \"$<d>-$<m>-$<y>\")",
        hint: "Reference the groups by name in the replacement: \"$<d>-$<m>-$<y>\".",
      },
      {
        prompt: "Named groups are reachable by name via interop. Write the expression that extracts the \"year\" group: build (.exec #\"(?<year>\\d{4})\" \"1991\"), then reach its -groups -year.",
        starter: "(.. (.exec #\"\" \"\") -groups -year)",
        tests: ["(= answer \"1991\")"],
        solution: "(.. (.exec #\"(?<year>\\d{4})\" \"1991\") -groups -year)",
        hint: "(.. (.exec #\"(?<year>\\d{4})\" \"1991\") -groups -year).",
      },
    ],
  },

  {
    id: "regex-golf",
    title: "Regex Golf",
    group: "Challenge",
    summary: "Write the shortest pattern that fits — a classic puzzle.",
    content: [
      { t: "p", text: "'Regex golf' is a game: write a pattern that matches EVERY string in a 'winners' list and NONE in a 'losers' list — ideally as short as possible. It's a great way to sharpen everything you've learned." },
      { t: "p", text: "Here's how we'll check a pattern: it must match all winners and no losers. Run this to see the idea — [true false] would mean 'matched all winners' and 'matched a loser' (we want the second to be false)." },
      { t: "code", code: "(let [win  [\"book\" \"hello\" \"letter\"]\n      lose [\"cat\" \"world\" \"code\"]\n      p    #\"(.)\\1\"]           ;; a repeated character\n  [(every?    #(re-find p %) win)\n   (not-any?  #(re-find p %) lose)])" },
      { t: "p", text: "That pattern (.)\\1 uses a backreference: \\1 means 'the same text that group 1 just matched'. So (.)\\1 finds any character immediately repeated — a double letter. Backreferences are handy for 'the same thing again'." },
      { t: "h", text: "Your turn" },
      { t: "p", text: "The exercises below are golf holes. In each, define answer as a pattern (#\"…\") that passes both checks. Aim for short and clear." },
      { t: "note", text: "Tip: re-find checks 'does the pattern appear anywhere?'; re-matches checks 'is the WHOLE string this pattern?'. The checks tell you which is being used." },
    ],
    exercises: [
      {
        prompt: "HOLE 1 (uses re-find). Match strings that contain a doubled character. Winners: \"book\" \"hello\" \"letter\". Losers: \"cat\" \"world\" \"code\".",
        starter: "#\"\"",
        tests: [
          "(every? #(re-find answer %) [\"book\" \"hello\" \"letter\"])",
          "(not-any? #(re-find answer %) [\"cat\" \"world\" \"code\"])",
        ],
        golf: { id: "double", par: 5 },
        solution: "#\"(.)\\1\"",
        hint: "A character, then a backreference to it: #\"(.)\\1\".",
      },
      {
        prompt: "HOLE 2 (uses re-matches). Match strings made ENTIRELY of non-vowels. Winners: \"rhythm\" \"xyz\" \"brr\". Losers: \"cat\" \"io\" \"hey\".",
        starter: "#\"\"",
        tests: [
          "(every? #(re-matches answer %) [\"rhythm\" \"xyz\" \"brr\"])",
          "(not-any? #(re-matches answer %) [\"cat\" \"io\" \"hey\"])",
        ],
        golf: { id: "novowel", par: 9 },
        solution: "#\"[^aeiou]+\"",
        hint: "One or more characters that are NOT vowels: #\"[^aeiou]+\".",
      },
      {
        prompt: "HOLE 3 (uses re-find). Match a hashtag: a # followed by one or more word characters. Winners: \"#regex\" \"#fun123\" \"#a\". Losers: \"#\" \"no_hash\" \"# space\".",
        starter: "#\"\"",
        tests: [
          "(every? #(re-find answer %) [\"#regex\" \"#fun123\" \"#a\"])",
          "(not-any? #(re-find answer %) [\"#\" \"no_hash\" \"# space\"])",
        ],
        golf: { id: "hashtag", par: 4 },
        solution: "#\"#\\w+\"",
        hint: "A literal # then one-or-more word chars: #\"#\\w+\".",
      },
      {
        prompt: "HOLE 4 (uses re-matches). Match strings of EXACTLY three word characters. Winners: \"cat\" \"a1b\" \"___\". Losers: \"ca\" \"cats\" \"a b\".",
        starter: "#\"\"",
        tests: [
          "(every? #(re-matches answer %) [\"cat\" \"a1b\" \"___\"])",
          "(not-any? #(re-matches answer %) [\"ca\" \"cats\" \"a b\"])",
        ],
        golf: { id: "three", par: 5 },
        solution: "#\"\\w{3}\"",
        hint: "Exactly three word characters: #\"\\w{3}\".",
      },
    ],
  },

  {
    id: "golf-leaderboard",
    title: "Capstone: Golf Leaderboard",
    group: "Challenge",
    summary: "Your shortest solutions, ranked — chase gold on every hole.",
    content: [
      { t: "p", text: "Regex golf rewards brevity. This live leaderboard records the SHORTEST passing pattern you've submitted for each hole in the previous lesson, and awards a medal when you match par." },
      { t: "golf-board" },
      { t: "note", text: "Go back to 'Regex Golf' and try to shrink your patterns. Every time you submit a shorter pattern that still passes, your best score here updates automatically. Matching par earns gold — can you get gold on all four holes?" },
      { t: "p", text: "Par counts only the characters inside the #\"…\". Beating par is rare; matching it is a win. If you want a clean slate, use the Reset scores button above." },
    ],
    exercises: [],
  },
];
